/*
	`useNotifier` returns a notifier function that can be used to add a notification to the
	Notification panel (not a growl), via socket message, and then update it after some
	async process resolves.

	The notifier function takes 4 arguments:
		- promise
			- A `.then`able promise
		- topic
			- The topic for the notification, given to the socket controller
		- message
			- the message that's initially added to the socket message list
		- transform
			- A function that takes the resolved value, the existing message and returns
		a new message to replace it.
			- If omitted, it will default to just updating the `message.status`, depending on
			the resolved value's `success` property, which will generally work with our
			thunk setup and AJAX middleware.

	##Example usage:

		Most commonly, we can just pass the notifier a dispatched action as the promise:
		```
			const notifier = useNotifier();

			const DOWNLOAD_TOPIC = "Download Assets";
			const message = {
				origin: "HOPI",
				task: "Download Started",
				status: "In Progress",
				data: {                      // data to use in the formatter component
					fileName,
					libraryName,
				},
				component: DownloadAssets,   // component that renders the notification
			};                               // see widgets/messages/DownloadAssets.js for example

			notifier(
				dispatch(downloadAssets(libraryId, assetArray, assetType.toLowerCase(), fileName)),
				DOWNLOAD_TOPIC,
				message
			);
		```

		For more advanced cases, we can add a messageTransform:
		```
			notifier(
				dispatch(saveCreatives()),
				{
					origin: "HOPI",
					task: "Download Started",
					status: "In Progress",
					data: {
						creatives: [],
					},
				},
				component: UpdatedCreatives,
				(response, message) => ({
					// This will do our normal status update without repeating the default logic
					...defaultTransformMessage(response, message),
					data: {
						creatives: response?.payload?.data || [],
					},
				})
			);
		```
*/

import useNotifications from "./useNotifications";

export const defaultTransformMessage = (response, message) => {
	return {
		...message,
		status: response.success ? "Success" : "Failed",
	};
};

export default function useNotifier() {
	const { addMessage, transformMessage } = useNotifications();

	return function(promise, topic, message, messageTransform = defaultTransformMessage) {
		const messageId = addMessage(topic, message);

		promise.then((response) => {
			transformMessage(
				topic,
				messageId,
				message => messageTransform(response, message)
			);
		});
	};
}
